import { useEffect, useMemo, useState } from "react";
import { differenceInYears, parseISO } from "date-fns";

import { Button } from "components/ui/Button";
import { Dialog, DialogFooter } from "components/ui/Dialog";
import { Select } from "components/ui/Select/Select";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { calculateCreatinineClearance } from "utils/creatinineClearance";

import styles from "./CreatinineClearanceCalculatorModal.module.css";

const normalizeDecimal = (value) =>
  value ? String(value).replace(",", ".") : "";
const parseNumeric = (value) => parseFloat(String(value).replace(",", "."));

export const CreatinineClearanceCalculatorModal = ({
  isOpen,
  onClose,
  onConfirm,
  prefillWeight,
  prefillSerumCreatinine,
  prefillHeight,
}) => {
  const { data: patient } = usePatientData();

  const patientAge = patient?.dateOfBirth
    ? differenceInYears(new Date(), parseISO(patient.dateOfBirth))
    : null;
  const patientSex = patient?.gender?.toUpperCase() ?? null;

  const [sex, setSex] = useState(patientSex ?? "");
  const [weight, setWeight] = useState(normalizeDecimal(prefillWeight));
  const [serumCreatinine, setSerumCreatinine] = useState(
    normalizeDecimal(prefillSerumCreatinine)
  );
  const [height, setHeight] = useState(normalizeDecimal(prefillHeight));
  const [fieldErrors, setFieldErrors] = useState({});

  // Keep the calculator's inputs in sync with the form when (re)opened, so a
  // value typed into the Serum Creatinine field shows here (and vice versa).
  useEffect(() => {
    if (!isOpen) return;

    setWeight(normalizeDecimal(prefillWeight));
    setSerumCreatinine(normalizeDecimal(prefillSerumCreatinine));
    setHeight(normalizeDecimal(prefillHeight));
    setFieldErrors({});
  }, [isOpen, prefillWeight, prefillSerumCreatinine, prefillHeight]);

  const result = useMemo(
    () =>
      calculateCreatinineClearance({
        age: patientAge,
        sex,
        weightKg: parseNumeric(weight),
        serumCreatinine: parseNumeric(serumCreatinine),
        heightCm: parseNumeric(height),
      }),
    [patientAge, sex, weight, serumCreatinine, height]
  );

  const handleClose = () => {
    setFieldErrors({});
    onClose();
  };

  const handleConfirm = () => {
    const errors = {};
    if (!patientAge) errors.patient = "Patient age is not available";
    if (!sex) errors.sex = "Sex is required";
    if (!weight) errors.weight = "Weight is required";
    if (!height) errors.height = "Height is required";
    if (!serumCreatinine) errors.serumCreatinine = "Serum creatinine is required";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (result == null) {
      setFieldErrors({ patient: "Could not calculate — check the values" });
      return;
    }

    setFieldErrors({});
    onConfirm({ creatinineClearance: String(result), serumCreatinine });
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title="Creatinine Clearance (Cockcroft-Gault)"
      width={620}
    >
      <div className={styles.body}>
        <div className={styles.patientInfo}>
          {!!patientAge && (
            <span className={styles.patientTag}>Age: {patientAge} yrs</span>
          )}
          {!!patientSex && (
            <span className={styles.patientTag}>
              {patientSex === "MALE" ? "Male" : "Female"}
            </span>
          )}
        </div>
        {!!fieldErrors.patient && (
          <p className={styles.fieldError}>{fieldErrors.patient}</p>
        )}

        {!patientSex && (
          <div>
            <span className={styles.fieldLabel}>Sex</span>
            <Select
              options={[
                { label: "Male", value: "MALE" },
                { label: "Female", value: "FEMALE" },
              ]}
              value={sex}
              onChange={(value) => {
                setSex(value);
                setFieldErrors((prev) => ({ ...prev, sex: undefined }));
              }}
              error={!!fieldErrors.sex}
            />
            {!!fieldErrors.sex && (
              <p className={styles.fieldError}>{fieldErrors.sex}</p>
            )}
          </div>
        )}

        <div className={styles.grid2}>
          <div>
            <span className={styles.fieldLabel}>Weight</span>
            <div className={styles.inputWithUnit}>
              <input
                type="text"
                inputMode="decimal"
                className={`${styles.input} ${
                  fieldErrors.weight ? styles.inputError : ""
                }`}
                placeholder="e.g. 80"
                value={weight}
                onChange={(e) => {
                  setWeight(normalizeDecimal(e.target.value));
                  setFieldErrors((prev) => ({ ...prev, weight: undefined }));
                }}
              />
              <span className={styles.inputUnit}>kg</span>
            </div>
            {!!fieldErrors.weight && (
              <p className={styles.fieldError}>{fieldErrors.weight}</p>
            )}
          </div>
          <div>
            <span className={styles.fieldLabel}>Height</span>
            <div className={styles.inputWithUnit}>
              <input
                type="text"
                inputMode="decimal"
                className={`${styles.input} ${
                  fieldErrors.height ? styles.inputError : ""
                }`}
                placeholder="e.g. 180"
                value={height}
                onChange={(e) => {
                  setHeight(normalizeDecimal(e.target.value));
                  setFieldErrors((prev) => ({ ...prev, height: undefined }));
                }}
              />
              <span className={styles.inputUnit}>cm</span>
            </div>
            {!!fieldErrors.height && (
              <p className={styles.fieldError}>{fieldErrors.height}</p>
            )}
          </div>
          <div>
            <span className={styles.fieldLabel}>Serum Creatinine</span>
            <div className={styles.inputWithUnit}>
              <input
                type="text"
                inputMode="decimal"
                className={`${styles.input} ${
                  fieldErrors.serumCreatinine ? styles.inputError : ""
                }`}
                placeholder="e.g. 90"
                value={serumCreatinine}
                onChange={(e) => {
                  setSerumCreatinine(normalizeDecimal(e.target.value));
                  setFieldErrors((prev) => ({
                    ...prev,
                    serumCreatinine: undefined,
                  }));
                }}
              />
              <span className={styles.inputUnit}>µmol/L</span>
            </div>
            {!!fieldErrors.serumCreatinine && (
              <p className={styles.fieldError}>{fieldErrors.serumCreatinine}</p>
            )}
          </div>
        </div>

        {result != null && (
          <div className={styles.result}>
            <span className={styles.resultLabel}>Creatinine Clearance</span>
            <span className={styles.resultScore}>{result} mL/min</span>
          </div>
        )}

      </div>
      <DialogFooter>
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleConfirm}>Use result</Button>
      </DialogFooter>
    </Dialog>
  );
};
